import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiFetch } from '../api/client';

import tealBack from '../Images/teal_dback.png';
import rypilLo from '../Images/rypilLo.png';

const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (value) => value.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { key: 'lowercase', label: 'One lowercase letter', test: (value) => /[a-z]/.test(value) },
  { key: 'digit', label: 'One number', test: (value) => /\d/.test(value) },
  { key: 'special', label: 'One symbol', test: (value) => /[^A-Za-z0-9]/.test(value) },
];

function getPasswordRuleState(value) {
  return PASSWORD_RULES.map((rule) => ({
    ...rule,
    met: rule.test(value),
  }));
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const trimmedPwd = (password || '').trim();
  const passwordRules = getPasswordRuleState(trimmedPwd);
  const passwordStarted = trimmedPwd.length > 0;
  const passwordValid = passwordRules.every((rule) => rule.met);

  const handleReset = async (e) => {
    e.preventDefault();
    setErrMsg('');
    setInfoMsg('');

    if (!trimmedPwd || !passwordValid) {
      setErrMsg('Password must be at least 8 characters and include uppercase, lowercase, number, and symbol.');
      return;
    }
    if (trimmedPwd !== (confirm || '').trim()) {
      setErrMsg('Passwords do not match.');
      return;
    }

    if (!token) {
      setErrMsg('Reset token missing. Use the link from your email.');
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: trimmedPwd }),
      });
      setInfoMsg('Password updated. You can now login.');
      setTimeout(() => navigate('/login', { replace: true }), 800);
    } catch (e) {
      console.error(e);
      const msg = e?.data?.error || e?.data?.message || e?.message || 'Please try again.';
      setErrMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        backgroundImage: `url(${tealBack})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        height: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontFamily: 'Helvetica, Arial, sans-serif',
        color: 'white',
      }}
    >
      <div
        style={{ position: 'absolute', inset: 0, borderRadius: '24px', overflow: 'hidden', pointerEvents: 'none' }}
      />

      <div
        style={{
          position: 'absolute',
          top: '64px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          letterSpacing: '1px',
        }}
      >
        <span style={{ fontSize: 30, fontWeight: 600 }}>
          <span className="rypil-word">rypil</span>{' '}
          <span style={{ fontWeight: 400 }}>Reset Password</span>
        </span>
      </div>

      <form
        onSubmit={handleReset}
        style={{
          width: 'min(90vw, 540px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 22,
          marginTop: '40px',
        }}
      >
        <input
          type="password"
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="login-input"
          style={{
            width: '100%',
            height: 72,
            borderRadius: 20,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: 'white',
            fontSize: 22,
            padding: '0 28px',
            outline: 'none',
          }}
        />

        <input
          type="password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="login-input"
          style={{
            width: '100%',
            height: 72,
            borderRadius: 20,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: 'white',
            fontSize: 22,
            padding: '0 28px',
            outline: 'none',
          }}
        />

        <p
          style={{ alignSelf: 'flex-start', marginTop: -10, marginBottom: 10, opacity: 0.9, fontSize: 16 }}
        >
          Enter your new password. Use the email link to load the token.
        </p>

        {passwordStarted && (
          <div
            style={{
              alignSelf: 'flex-start',
              width: '100%',
              marginTop: -6,
              marginBottom: 6,
              padding: '14px 16px',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, opacity: 0.95 }}>
              Password requirements
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {passwordRules.map((rule) => (
                <div
                  key={rule.key}
                  style={{
                    fontSize: 14,
                    color: rule.met ? '#D9FFE0' : 'rgba(255,255,255,0.78)',
                  }}
                >
                  {rule.met ? '✓' : '•'} {rule.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {errMsg && (
          <p style={{ color: '#ffdddd', alignSelf: 'flex-start', marginTop: -4 }}>{errMsg}</p>
        )}
        {infoMsg && (
          <p style={{ color: '#ddffdd', alignSelf: 'flex-start', marginTop: -4 }}>{infoMsg}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: 320,
            height: 72,
            borderRadius: 20,
            border: 'none',
            cursor: 'pointer',
            fontSize: 24,
            fontWeight: 600,
            color: '#1d1d1d',
            background: '#EBA477',
            boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
            marginTop: 10,
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? 'Updating...' : 'Update Password'}
        </button>

        <div style={{ marginTop: 14, fontSize: 18 }}>
          Want to login?{' '}
          <Link
            to="/login"
            style={{ color: 'white', textDecoration: 'none', borderBottom: '2px solid #EBA477', paddingBottom: 2 }}
          >
            Login
          </Link>
        </div>
      </form>

      <img
        src={rypilLo}
        alt="rypil mark"
        style={{ position: 'absolute', bottom: 32, left: 32, height: 56, opacity: 0.95 }}
      />

      <style>{`
        .login-input::placeholder { color: rgba(255,255,255,0.75); }
        .login-input:-ms-input-placeholder { color: rgba(255,255,255,0.75); }
        .login-input::-ms-input-placeholder { color: rgba(255,255,255,0.75); }
      `}</style>
    </div>
  );
}

export default ResetPassword;
